import { squarify, type Rect } from './chartGeometry'

/** Pixel estimates for a `text-xs` label inside a tile; deliberately generous so an estimate that passes never truncates. */
export interface TileMetrics {
  charWidth: number
  lineHeight: number
  padX: number
  padY: number
  maxLines: number
}

const TILE_METRICS: TileMetrics = { charWidth: 7.2, lineHeight: 16, padX: 15, padY: 15, maxLines: 3 }

export interface TreemapTile {
  x: number
  y: number
  w: number
  h: number
  label: string
  value: number
  /** The bucket this tile draws, or null for the "Other" tile. */
  index: number | null
}

/** Lines a label wraps to on word boundaries in `width` px, or null when a single word is wider than the tile. */
export function labelLines(label: string, width: number, charWidth = TILE_METRICS.charWidth): number | null {
  const words = label.split(/\s+/).filter(Boolean)
  if (words.length === 0) return 1
  let lines = 1
  let used = 0
  for (const word of words) {
    const len = word.length * charWidth
    if (len > width) return null
    const next = used === 0 ? len : used + charWidth + len
    if (next <= width) used = next
    else {
      lines += 1
      used = len
    }
  }
  return lines
}

/** Whether the name, wrapped, and the value below it fit inside a tile without clipping. */
export function tileFits(
  tile: { w: number; h: number },
  label: string,
  valueText: string,
  m: TileMetrics = TILE_METRICS,
): boolean {
  const inner = tile.w - m.padX
  const lines = labelLines(label, inner, m.charWidth)
  if (lines === null || lines > m.maxLines) return false
  if (valueText.length * m.charWidth > inner) return false
  return (lines + 1) * m.lineHeight + m.padY <= tile.h
}

/** Percent of --viz-s1 a tile is filled with, deepest for the largest; above 80 white text on the dark-mode fill drops under 4.5:1. */
export const TILE_SHADE = { max: 80, min: 20 }

/** The fill strength of the tile ranked `rank` (0 = largest) among `count` tiles. */
export function tileShade(rank: number, count: number): number {
  return TILE_SHADE.max - (rank / Math.max(1, count - 1)) * (TILE_SHADE.max - TILE_SHADE.min)
}

/** Wider than this, squarify degenerates into a single strip of columns. */
const STRIP_ASPECT = 2

/** Squarified rects that read as a 2-D mosaic: a wide box with four or more parts is split into two balanced horizontal bands first. */
export function mosaic(values: number[], width: number, height: number): Rect[] {
  const parts = values.map((value, index) => ({ value, index })).filter((p) => p.value > 0)
  if (parts.length < 4 || width / height <= STRIP_ASPECT) return squarify(values, width, height)
  const bands: { value: number; index: number }[][] = [[], []]
  const sums = [0, 0]
  for (const p of [...parts].sort((a, b) => b.value - a.value || a.index - b.index)) {
    const k = sums[0]! <= sums[1]! ? 0 : 1
    bands[k]!.push(p)
    sums[k]! += p.value
  }
  const total = sums[0]! + sums[1]!
  const topHeight = (sums[0]! / total) * height
  return bands.flatMap((band, k) =>
    squarify(
      band.map((p) => p.value),
      width,
      k === 0 ? topHeight : height - topHeight,
    ).map((r) => ({ ...r, y: r.y + (k === 0 ? 0 : topHeight), index: band[r.index]!.index })),
  )
}

/**
 * Squarified tiles for `buckets` in a `width` by `height` px box, folding the smallest into one "Other" tile until every
 * tile's name and value fit; returns the tiles and the bucket indexes folded into "Other".
 */
export function treemapTiles(
  buckets: readonly { label: string; value: number }[],
  width: number,
  height: number,
  valueText: (n: number) => string,
  otherLabel: string,
  m: TileMetrics = TILE_METRICS,
): { tiles: TreemapTile[]; folded: number[] } {
  const order = buckets
    .map((b, index) => ({ ...b, index }))
    .filter((b) => b.value > 0)
    .sort((a, b) => b.value - a.value || a.index - b.index)
  if (order.length === 0 || width <= 0 || height <= 0) return { tiles: [], folded: [] }

  let last: { tiles: TreemapTile[]; folded: number[] } = { tiles: [], folded: [] }
  for (let keep = order.length; keep >= 0; keep -= 1) {
    const rest = order.slice(keep)
    // Folding one item into "Other" only renames it.
    if (rest.length === 1) continue
    const parts = order.slice(0, keep).map((b) => ({ label: b.label, value: b.value, index: b.index as number | null }))
    if (rest.length > 0) parts.push({ label: otherLabel, value: rest.reduce((sum, b) => sum + b.value, 0), index: null })
    const tiles = mosaic(
      parts.map((p) => p.value),
      width,
      height,
    ).map((r) => ({ x: r.x, y: r.y, w: r.w, h: r.h, ...parts[r.index]! }))
    last = { tiles, folded: rest.map((b) => b.index) }
    if (tiles.every((t) => tileFits(t, t.label, valueText(t.value), m))) return last
  }
  return last
}
